import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ScanFace } from 'lucide-react';
import { anamnesiVociAPI } from '../../services/api';
import { useSalvaCampo, conInvio } from '../../utils/salvaCampo';
import { useUscitaProtetta } from '../../utils/uscitaProtetta';
import StatoSalvataggio from '../StatoSalvataggio';
import { annunciaAnamnesiCambiata } from '../../utils/annunci';
import { DOMANDE_SI_NO } from './avvisiTestata';
import '../../styles/SchedaFisiologica.css';
import '../../styles/SchedaFisiologicaConsole.css';

/**
 * RiquadroRisposte — «Patologie cutanee» ed «Herpes» sotto le allergie
 * (Alex, 3/11/2026; migration 356): per ognuna Sì / No e, col Sì, un testo
 * facoltativo. Prima che qualcuno risponda: «non indicato», che non e' un No.
 *
 * SI SALVA DA SOLO (regola di Alex del 24/09/2026): la scelta appena
 * premuta, il testo all'uscita dal campo. Un campo per volta: il server
 * compone sopra la risposta corrente, e decide lui se correggere la riga
 * (e' di chi scrive) o scriverne una nuova segnando superata quella di un
 * altro. Le regole di chi scrive e chi corregge sono quelle delle voci.
 *
 * Col Sì la testata della cartella mostra l'avviso accanto alle allergie
 * (avvisiDellaTestata): per questo, dopo ogni salvataggio, si annuncia che
 * l'anamnesi e' cambiata e la testata si rilegge.
 *
 * @param {Array|null} risposte   le risposte correnti dalla lettura dell'anamnesi; null se non lette
 * @param {function}   modificabile  la regola della schermata delle voci (il server ricontrolla)
 * @param {function}   autoreDi      come si scrive l'autore, lo stesso delle voci
 */
const CONFERMA_NO = 'Il testo scritto si toglie. Procedo? Resta nel registro delle modifiche.';

const soloData = (v) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(v || ''));
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null;
};

const RiquadroRisposte = ({ pazienteId, risposte, modificabile, autoreDi, onErrore }) => {
    const [correnti, setCorrenti] = useState({});    // domanda -> riga corrente
    const [testi, setTesti] = useState({});          // domanda -> testo nel campo
    const [scelte, setScelte] = useState({});        // domanda -> 'si' | 'no' scelta a schermo
    const [rifiuti, setRifiuti] = useState({});      // domanda -> motivo dell'ultimo rifiuto
    const testiOra = useRef({});
    testiOra.current = testi;

    const { stato, salva, nonSalvati, salvatoAlle, segnaBase, uscita } = useSalvaCampo(
        useCallback(async (campo, valore) => {
            const [domanda, chiave] = campo.split(':');
            let r;
            try {
                r = await anamnesiVociAPI.rispondi(pazienteId, domanda, { [chiave]: valore });
            } catch (e) {
                setRifiuti((p) => ({ ...p, [domanda]: e?.response?.data?.message || null }));
                throw e;
            }
            setRifiuti((p) => ({ ...p, [domanda]: null }));
            const riga = r.data?.data?.risposta || null;
            setCorrenti((p) => ({ ...p, [domanda]: riga }));
            if (!r.data?.data?.invariata) annunciaAnamnesiCambiata();
            return r;
        }, [pazienteId]),
        null,
        /* Il riquadro si smonta senza uscire dal campo (cambio di linguetta,
           Ctrl K): il testo scritto si salva comunque, se e' cambiato. */
        { allaChiusura: () => {
            DOMANDE_SI_NO.forEach(({ domanda }) => {
                if (testiOra.current[domanda] !== undefined) salvaTesto(domanda, testiOra.current[domanda]);
            });
            return true;
        } });

    /* Le risposte arrivano dalla lettura dell'anamnesi: a ogni lettura nuova
       il riquadro riparte da li', e la base dei campi e' cio' che c'e'. */
    useEffect(() => {
        if (!Array.isArray(risposte)) return;
        const c = {}; const t = {}; const s = {};
        DOMANDE_SI_NO.forEach(({ domanda }) => {
            const riga = risposte.find((x) => x.domanda === domanda) || null;
            c[domanda] = riga;
            t[domanda] = riga?.testo || '';
            s[domanda] = riga?.risposta || null;
            segnaBase(`${domanda}:risposta`, riga?.risposta || null);
            segnaBase(`${domanda}:testo`, riga?.testo || null);
        });
        setCorrenti(c); setTesti(t); setScelte(s);
    }, [risposte, segnaBase]);

    useUscitaProtetta(nonSalvati.length > 0, nonSalvati.length || null, uscita);

    function salvaTesto(domanda, valore) {
        const t = String(valore ?? '').trim() || null;
        salva(`${domanda}:testo`, t);
    }

    const scegli = (domanda, valore) => {
        if (scelte[domanda] === valore) return;
        const testoScritto = String(testi[domanda] || '').trim();
        if (valore === 'no' && testoScritto && !window.confirm(CONFERMA_NO)) return;
        setScelte((p) => ({ ...p, [domanda]: valore }));
        if (valore === 'no') {
            /* Il server toglie il testo col no: la base del campo lo segue. */
            setTesti((p) => ({ ...p, [domanda]: '' }));
            segnaBase(`${domanda}:testo`, null);
        }
        salva(`${domanda}:risposta`, valore);
    };

    const elimina = async (domanda) => {
        const riga = correnti[domanda];
        if (!riga) return;
        if (!window.confirm('Eliminare questa risposta?\n\nLa domanda torna «non indicato».')) return;
        try {
            await anamnesiVociAPI.cancellaRisposta(riga.id);
            setCorrenti((p) => ({ ...p, [domanda]: null }));
            setScelte((p) => ({ ...p, [domanda]: null }));
            setTesti((p) => ({ ...p, [domanda]: '' }));
            segnaBase(`${domanda}:risposta`, null);
            segnaBase(`${domanda}:testo`, null);
            annunciaAnamnesiCambiata();
        } catch (e) {
            if (onErrore) onErrore(e?.response?.data?.message || 'Errore nell\'eliminazione');
        }
    };

    /* Due riquadri, uno per domanda, nella forma delle sezioni delle voci.
       DENTRO UN CONTENITORE SUO, div.av-risposte, che non disegna niente
       (display: contents, AnamnesiVoci.css e AnamnesiVociConsole.css): i due
       riquadri restano due sezioni dell'elenco, con lo stesso spazio fra
       loro, ma non prendono il posto delle sezioni che seguono nella prova
       dello stile di prima (provaStileClassico, che riconosce un elemento dal
       suo cammino e dal suo numero fra i fratelli uguali: due div.av-sezione
       in piu' rinumeravano quelle sotto). NO del beta del 3/11/2026. */
    if (!Array.isArray(risposte)) {
        return (
            <div className="av-risposte" data-riquadri="risposte">
                {DOMANDE_SI_NO.map(({ domanda, titolo }) => (
                    <div key={domanda} className="av-sezione" data-riquadro={domanda}>
                        <div className="av-sezione-testa">
                            <h3><ScanFace size={16} strokeWidth={1.8} aria-hidden="true" />{titolo}</h3>
                        </div>
                        <p className="av-vuoto">Non riesco a leggere la risposta: ricarica la scheda.</p>
                    </div>
                ))}
            </div>
        );
    }

    return (
        <div className="av-risposte" data-riquadri="risposte">
            {DOMANDE_SI_NO.map(({ domanda, titolo }) => {
                const riga = correnti[domanda] || null;
                const scelta = scelte[domanda] || null;
                const campi = [`${domanda}:risposta`, `${domanda}:testo`];
                return (
                    <div key={domanda} className="av-sezione" data-riquadro={domanda} data-salva-da-solo>
                        <div className="av-sezione-testa">
                            <h3><ScanFace size={16} strokeWidth={1.8} aria-hidden="true" />{titolo}</h3>
                        </div>
                        <StatoSalvataggio campi={campi} stato={stato} salvatoAlle={salvatoAlle}
                            onRiprova={() => nonSalvati.filter((c) => campi.includes(c)).forEach((c) => {
                                salva(c, c.endsWith(':testo') ? (String(testi[domanda] || '').trim() || null) : scelte[domanda]);
                            })} />
                        <div className="sf-spunte sf-spunte-riga" role="group" aria-label={titolo}>
                            {[{ v: 'si', e: 'Sì' }, { v: 'no', e: 'No' }].map((o) => (
                                <label key={o.v} className="sf-spunta">
                                    <input type="checkbox" data-risposta={o.v} checked={scelta === o.v}
                                           onChange={() => scegli(domanda, o.v)} />
                                    <span>{o.e}</span>
                                </label>
                            ))}
                            {!scelta && <span className="av-vuoto">non indicato</span>}
                        </div>
                        {scelta === 'si' && (
                            <div className="av-voce av-voce-modifica">
                                <textarea
                                    rows={2}
                                    aria-label={`${titolo}: descrizione (facoltativa)`}
                                    placeholder="Descrizione (facoltativa)"
                                    value={testi[domanda] || ''}
                                    onChange={(e) => setTesti((p) => ({ ...p, [domanda]: e.target.value }))}
                                    onBlur={(e) => salvaTesto(domanda, e.target.value)}
                                    onKeyDown={conInvio((e) => salvaTesto(domanda, e.target.value))}
                                />
                            </div>
                        )}
                        {riga && (
                            <div className="av-voce-piede">
                                <span className="av-firma">
                                    {autoreDi(riga)}
                                    {` · inserita il ${soloData(riga.created_at) || '—'}`}
                                    {riga.updated_at && soloData(riga.updated_at) !== soloData(riga.created_at)
                                        ? ` · aggiornata il ${soloData(riga.updated_at)}` : ''}
                                </span>
                                <div className="av-voce-azioni">
                                    {modificabile(riga) && (
                                        <button type="button" className="av-link av-link-pericolo" onClick={() => elimina(domanda)}>
                                            Elimina
                                        </button>
                                    )}
                                </div>
                            </div>
                        )}
                        {rifiuti[domanda] && (
                            <div className="av-errore-campo" role="alert"><span>Non salvato: {rifiuti[domanda]}</span></div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default RiquadroRisposte;
